import type { AnalyticsType } from '@repo/segment/types';
import { Vertical } from '@repo/supergraph-schemas/marketplace/constants';
import type { DriverType } from '@repo/supergraph-schemas/marketplace/types';
import type { SummaryTableRowType } from '@repo/supergraph-schemas/roundup/base/types';
import type { SummaryTableRowWithDrivers } from './types';

export const DEFAULT_RATING_TOOLTIP = `NerdWallet's ratings are determined by our editorial team. The scoring formulas take into account multiple data points for each financial product and service.`;

export const DEFAULT_HEADINGS = [
  { label: 'Bank/institution' },
  {
    label: 'NerdWallet rating',
    labelTooltip: DEFAULT_RATING_TOOLTIP,
  },
  {
    label: 'Monthly fee',
  },
  {
    label: 'APY',
  },
  {
    label: 'Bonus',
  },
];

export const APY_TREND_TOOLTIP_COPY =
  'APY Trend displays how the base APY for a product has changed over the last 90 days. It could be trending up, trending down, or stable (meaning no change).';

export const BASIC_HEADINGS = (marketplaceEntity: SummaryTableRowType['marketplaceEntity']) => [
  {
    label: 'Bank/institution',
  },
  {
    label: 'NerdWallet rating',
    labelTooltip:
      marketplaceEntity.productType === Vertical.Banking && marketplaceEntity?.starRatingTooltip
        ? marketplaceEntity.starRatingTooltip
        : DEFAULT_RATING_TOOLTIP,
  },
];

export const getHeadings = (
  marketplaceEntity: SummaryTableRowWithDrivers['marketplaceEntity'],
  isApyRateTrendTreatment1Enabled = false,
  isApyRateTrendTreatment2Enabled = false,
) => {
  const { drivers } = marketplaceEntity;
  const driverHeadings: { label: string; labelTooltip?: string }[] =
    drivers?.map((driver: DriverType) => ({
      label: driver.label,
      labelTooltip:
        driver.id?.includes('apy') && isApyRateTrendTreatment1Enabled
          ? APY_TREND_TOOLTIP_COPY
          : undefined,
    })) ?? [];

  if (isApyRateTrendTreatment2Enabled) {
    const apyIndex = driverHeadings.findIndex(heading => heading.label === 'APY');
    if (apyIndex !== -1) {
      driverHeadings.splice(apyIndex + 1, 0, {
        label: 'APY Trend',
        labelTooltip: APY_TREND_TOOLTIP_COPY,
      });
    }
  }

  return [...BASIC_HEADINGS(marketplaceEntity), ...driverHeadings];
};

type GetMarketplacePayload = {
  analytics: AnalyticsType;
  ctaLink: string;
  sectionName: string;
  sectionPosition: number;
};

export const getMarketplacePayload = ({
  analytics,
  ctaLink,
  sectionName,
  sectionPosition,
}: GetMarketplacePayload) => {
  return {
    destination_url: ctaLink,
    element_position: analytics.index,
    marketplace_institution_id: analytics.institutionId,
    marketplace_offer_id: analytics.offerId,
    marketplace_product_id: analytics.productId,
    marketplace_product_type: analytics.productType,
    monetizable: analytics.monetizable,
    section_name: sectionName,
    section_position: sectionPosition,
  };
};
